// Items, stacks and the inventory. The server owns every inventory and
// applies every change; the client repeats these rules to predict moves and shape its windows.
import { ArmorSlot, Binding, ContainerId, ItemQuality, JewelryKind, OffhandKind, ToolKind, WeaponClass } from '../constants';
import { type GameContent, type HealZone, type ItemDefinition, type ProjectileStats } from '../content';

export interface ItemStack { readonly item: number; readonly count: number; readonly bound: boolean }

export const emptyStack: ItemStack = Object.freeze({ item: 0, count: 0, bound: false });

export const stack = (item: number, count: number, bound = false): ItemStack => ({ item, count, bound });

export const isEmpty = (s: ItemStack) => s.item === 0 || s.count <= 0;

/** Whether two stacks can merge (same item, and both bound or both not). */
export const stacksWith = (a: ItemStack, b: ItemStack) => !isEmpty(a) && !isEmpty(b) && a.item === b.item && a.bound === b.bound;

export const itemDef = (id: number, c: GameContent): ItemDefinition | undefined => c.item(id);
export const isValidItem = (id: number, c: GameContent) => c.item(id) !== undefined;
export const maxStack = (id: number, c: GameContent) => c.item(id)?.maxStack ?? 0;
export const toolOf = (id: number, c: GameContent) => c.item(id)?.tool ?? ToolKind.None;
export const armorOf = (id: number, c: GameContent) => c.item(id)?.armor ?? ArmorSlot.None;
export const sellPriceOf = (id: number, c: GameContent) => c.item(id)?.sellPrice ?? 0;
export const bindingOf = (id: number, c: GameContent) => c.item(id)?.binding ?? Binding.None;
export const qualityOf = (id: number, c: GameContent) => c.item(id)?.quality ?? ItemQuality.Common;
export const isTool = (d: ItemDefinition) => d.tool !== ToolKind.None;
export const isOffhand = (d: ItemDefinition) => d.offhand !== OffhandKind.None;
/** The offhand kind a weapon class takes (None: a two-handed weapon takes nothing). */
export const offhandFor = (c: WeaponClass): OffhandKind =>
  c === WeaponClass.Staff ? OffhandKind.Spell : c === WeaponClass.Bow ? OffhandKind.Arrows : c === WeaponClass.OneHanded ? OffhandKind.Shield : OffhandKind.None;
/** Whether an offhand item may go beside a weapon (an item id; 0 = no weapon, which takes nothing). */
export function offhandFits(weapon: number, item: number, c: GameContent): boolean {
  const w = c.item(weapon)?.weapon, kind = c.item(item)?.offhand ?? OffhandKind.None;
  return !!w && kind !== OffhandKind.None && offhandFor(w.weaponClass) === kind;
}
export const isArmor = (d: ItemDefinition) => d.armor !== ArmorSlot.None;
export const isJewelry = (d: ItemDefinition) => d.jewelry !== JewelryKind.None;
/** The belt slot kind a piece of jewellery goes in. */
export const jewelrySlotKind = (k: JewelryKind) => (k === JewelryKind.Ring ? ToolKind.Ring : k === JewelryKind.Necklace ? ToolKind.Necklace : ToolKind.None);
export const isSellable = (d: ItemDefinition) => d.sellPrice > 0;
export const isConsumable = (d: ItemDefinition) => d.heals > 0;

/**
 * Taking a hit: armour scales damage by scale / (scale + armour) (20 armour halves it). Blocking with a shield adds its
 * block armour and costs stamina per hit; a block without the stamina for it still holds, but the hit stuns.
 */
export const Defense = {
  armorScale: 20,
  /** Stamina (motor units: 30 a point) each blocked hit costs. */
  blockStaminaUnits: 25 * 30,
  /** Ticks a hit on a block without the stamina for it stuns for. */
  stunTicks: 2 * 30,
  /** Damage after armour (float32, like health). */
  damageTaken: (damage: number, armor: number) =>
    Math.fround(Math.fround(damage * Defense.armorScale) / Math.fround(Defense.armorScale + Math.max(0, armor))),
  /** The share of damage armour stops, 0..1 (the character sheet's figure). */
  reduction: (armor: number) => 1 - Defense.armorScale / (Defense.armorScale + Math.max(0, armor)),
};

/** Seconds between uses of consumables (one timer for all of them). */
export const consumableCooldownSeconds = 5;

/** Every item, by id. */
export const allItems = (c: GameContent): ItemDefinition[] => [...c.items.values()].sort((a, b) => a.id - b.id);

/** The worn-armour slots (the armour container), in order. */
export const Equipment = {
  slots: [ArmorSlot.Head, ArmorSlot.Chest, ArmorSlot.Legs, ArmorSlot.Feet] as readonly ArmorSlot[],
  slotFor: (slot: ArmorSlot) => Equipment.slots.indexOf(slot),
  /** The Armor stat: the sum of what's worn, a shield in the offhand and jewellery. */
  armorValue: (inv: Inventory) => [...inv.armor, ...[ToolBelt.offhand, ToolBelt.necklace, ...ToolBelt.rings].map(i => inv.belt[i])]
    .reduce((n, s) => n + (isEmpty(s) ? 0 : inv.content.item(s.item)?.armorValue ?? 0), 0),
  /** The shield in the offhand slot (an item id), or 0: what lets a player block. */
  shield(inv: Inventory): number {
    const s = inv.belt[ToolBelt.offhand];
    return !isEmpty(s) && inv.content.item(s.item)?.offhand === OffhandKind.Shield && offhandFits(inv.weaponItem, s.item, inv.content) ? s.item : 0;
  },
  /** Armour that only counts while blocking: the shield's. */
  blockArmor: (inv: Inventory) => inv.content.item(Equipment.shield(inv))?.blockArmor ?? 0,
  slotName(slot: number): string {
    const k = Equipment.slots[slot];
    return k === ArmorSlot.Head ? 'Head' : k === ArmorSlot.Chest ? 'Chest' : k === ArmorSlot.Legs ? 'Pants' : k === ArmorSlot.Feet ? 'Boots' : String(k);
  },
};

/** The tool belt: one slot per tool kind, in saved order (append only; the character sheet lays them out its own way). */
export const ToolBelt = {
  slots: [ToolKind.Axe, ToolKind.Pickaxe, ToolKind.Sword, ToolKind.Net, ToolKind.Offhand, ToolKind.Necklace, ToolKind.Ring, ToolKind.Ring] as readonly ToolKind[],
  /** The (first) slot of a kind. */
  slotFor: (kind: ToolKind) => ToolBelt.slots.indexOf(kind),
  get weapon() { return ToolBelt.slotFor(ToolKind.Sword); },
  get offhand() { return ToolBelt.slotFor(ToolKind.Offhand); },
  get necklace() { return ToolBelt.slotFor(ToolKind.Necklace); },
  get rings(): number[] { return ToolBelt.slots.flatMap((k, i) => (k === ToolKind.Ring ? [i] : [])); },
  slotName(slot: number): string {
    const k = ToolBelt.slots[slot];
    return k === ToolKind.Axe ? 'Axe' : k === ToolKind.Pickaxe ? 'Pickaxe' : k === ToolKind.Sword ? 'Weapon' : k === ToolKind.Net ? 'Net'
      : k === ToolKind.Offhand ? 'Offhand' : k === ToolKind.Necklace ? 'Necklace' : k === ToolKind.Ring ? `Ring ${ToolBelt.rings.indexOf(slot) + 1}` : String(k);
  },
};

export class Inventory {
  static readonly mainColumns = 6;
  static readonly mainRows = 4;
  static readonly mainSize = 24;

  readonly main: ItemStack[] = new Array(Inventory.mainSize).fill(emptyStack);
  readonly belt: ItemStack[] = new Array(ToolBelt.slots.length).fill(emptyStack);
  readonly armor: ItemStack[] = new Array(Equipment.slots.length).fill(emptyStack);
  /** Bumped on every change. */
  version = 0;

  constructor(private readonly definitions: GameContent) {}
  get content(): GameContent { return this.definitions; }

  container(c: ContainerId): ItemStack[] | null {
    return c === ContainerId.Main ? this.main : c === ContainerId.Belt ? this.belt : c === ContainerId.Armor ? this.armor : null;
  }

  get(c: ContainerId, index: number): ItemStack {
    const arr = this.container(c);
    return arr && index >= 0 && index < arr.length ? arr[index] : emptyStack;
  }

  /**
   * Whether a stack may occupy a slot. Belt and armour slots only take their own kind; the offhand slot takes offhand
   * items, and only what `weapon` (the item in the weapon slot) takes, unless weapon is undefined (kind only).
   */
  static canPlace(c: ContainerId, index: number, s: ItemStack, definitions: GameContent, weapon?: number): boolean {
    if (isEmpty(s)) return true;
    if (!isValidItem(s.item, definitions) || s.count > maxStack(s.item, definitions)) return false;
    switch (c) {
      case ContainerId.Main: return index >= 0 && index < Inventory.mainSize;
      case ContainerId.Belt:
        if (index === ToolBelt.offhand)
          return weapon === undefined ? isOffhand(definitions.item(s.item)!) : offhandFits(weapon, s.item, definitions);
        if (ToolBelt.slots[index] === ToolKind.Necklace || ToolBelt.slots[index] === ToolKind.Ring)
          return jewelrySlotKind(definitions.item(s.item)!.jewelry) === ToolBelt.slots[index];
        return index >= 0 && index < ToolBelt.slots.length && toolOf(s.item, definitions) === ToolBelt.slots[index];
      case ContainerId.Armor: return index >= 0 && index < Equipment.slots.length && armorOf(s.item, definitions) === Equipment.slots[index];
      default: return false;
    }
  }

  /** The unique item a move would leave equipped twice, or null. */
  uniqueClash(fromC: ContainerId, from: number, toC: ContainerId, to: number): ItemDefinition | null {
    const a = this.get(fromC, from), b = this.get(toC, to);
    const after = (c: ContainerId, i: number, s: ItemStack) => (c === toC && i === to ? a : c === fromC && i === from ? b : s);
    const seen = new Set<number>();
    for (const c of [ContainerId.Belt, ContainerId.Armor]) {
      const arr = this.container(c)!;
      for (let i = 0; i < arr.length; i++) {
        const s = after(c, i, arr[i]), def = isEmpty(s) ? undefined : this.content.item(s.item);
        if (!def?.unique) continue;
        if (seen.has(s.item)) return def;
        seen.add(s.item);
      }
    }
    return null;
  }

  /** The item in the weapon slot (0 = none). */
  get weaponItem(): number { return isEmpty(this.belt[ToolBelt.weapon]) ? 0 : this.belt[ToolBelt.weapon].item; }

  /**
   * Moves (or merges, or swaps) the stack in one slot onto another. A new weapon (or none) that can't use what's in
   * the offhand puts it back in the bag; with no room for it, nothing moves. Nor does a move that would equip a
   * unique item twice.
   */
  tryMove(fromC: ContainerId, from: number, toC: ContainerId, to: number): boolean {
    const src = this.container(fromC), dst = this.container(toC);
    if (!src || !dst || from < 0 || from >= src.length || to < 0 || to >= dst.length) return false;
    if (fromC === toC && from === to) return false;
    const a = src[from], b = dst[to];
    if (isEmpty(a)) return false;
    if (stacksWith(b, a)) {
      const moved = Math.min(a.count, maxStack(a.item, this.content) - b.count);
      if (moved <= 0) return false;
      dst[to] = { ...b, count: b.count + moved };
      src[from] = a.count - moved > 0 ? { ...a, count: a.count - moved } : emptyStack;
      this.version++;
      return true;
    }
    const weapon = this.weaponItem;
    if (!Inventory.canPlace(toC, to, a, this.content, weapon) || !Inventory.canPlace(fromC, from, b, this.content, weapon)) return false;
    if (this.uniqueClash(fromC, from, toC, to)) return false;
    dst[to] = bindIfEquipped(toC, a, this.content);
    src[from] = bindIfEquipped(fromC, b, this.content);
    if (this.weaponItem !== weapon && !this.settleOffhand()) {
      src[from] = a; dst[to] = b;
      return false;
    }
    this.version++;
    return true;
  }

  /** Puts an offhand item the weapon can't use into the bag. False if there's no room for it. */
  private settleOffhand(): boolean {
    const slot = ToolBelt.offhand, s = this.belt[slot];
    if (isEmpty(s) || offhandFits(this.weaponItem, s.item, this.content)) return true;
    const free = this.main.findIndex(isEmpty);
    if (free < 0) return false;
    this.main[free] = s;
    this.belt[slot] = emptyStack;
    return true;
  }

  /** Removes a stack outright (destroying it). False if the slot no longer holds item. */
  destroy(c: ContainerId, index: number, item: number): boolean {
    const s = this.get(c, index);
    if (isEmpty(s) || s.item !== item) return false;
    this.set(c, index, emptyStack);
    return true;
  }

  /**
   * The slot an item equips into: its belt or worn slot; a ring goes over the same unique ring, else in an empty ring
   * slot, else the first. Null for items that aren't equipped.
   */
  equipSlot(def: ItemDefinition): [ContainerId, number] | null {
    if (isOffhand(def)) return [ContainerId.Belt, ToolBelt.offhand];
    if (def.jewelry === JewelryKind.Necklace) return [ContainerId.Belt, ToolBelt.necklace];
    if (def.jewelry === JewelryKind.Ring) {
      const rings = ToolBelt.rings;
      return [ContainerId.Belt, rings.find(i => def.unique && this.belt[i].item === def.id) ?? rings.find(i => isEmpty(this.belt[i])) ?? rings[0]];
    }
    const slot = ToolBelt.slotFor(def.tool), worn = Equipment.slotFor(def.armor);
    return isTool(def) && slot >= 0 ? [ContainerId.Belt, slot] : isArmor(def) && worn >= 0 ? [ContainerId.Armor, worn] : null;
  }

  /** Shift-click: anything in the bag to the slot it equips into (equipSlot); anything equipped back to the grid. */
  quickMove(c: ContainerId, index: number): boolean {
    const s = this.get(c, index);
    if (isEmpty(s)) return false;
    if (c === ContainerId.Main) {
      const target = this.equipSlot(this.content.item(s.item)!);
      return !!target && this.tryMove(ContainerId.Main, index, ...target);
    }
    const empty = this.main.findIndex(isEmpty);
    return empty >= 0 && this.tryMove(c, index, ContainerId.Main, empty);
  }

  /** How many of an item would fit if added now. */
  spaceFor(item: number): number {
    const max = maxStack(item, this.content);
    if (max === 0) return 0;
    const bound = bindingOf(item, this.content) === Binding.OnPickup;
    let space = 0;
    for (const s of this.main) {
      if (isEmpty(s)) space += max;
      else if (s.item === item && s.bound === bound) space += max - s.count;
    }
    return space;
  }

  /** Adds items to existing stacks first, then empty slots. Returns how many didn't fit. */
  add(item: number, count: number): number {
    const max = maxStack(item, this.content);
    if (max === 0 || count <= 0) return count;
    const bound = bindingOf(item, this.content) === Binding.OnPickup;
    let remaining = count;
    for (let pass = 0; pass < 2 && remaining > 0; pass++) {
      for (let i = 0; i < this.main.length && remaining > 0; i++) {
        const s = this.main[i];
        const match = pass === 0 ? !isEmpty(s) && s.item === item && s.bound === bound : isEmpty(s);
        if (!match) continue;
        const have = isEmpty(s) ? 0 : s.count;
        const put = Math.min(remaining, max - have);
        if (put <= 0) continue;
        this.main[i] = { item, count: have + put, bound };
        remaining -= put;
      }
    }
    if (remaining !== count) this.version++;
    return remaining;
  }

  countOf(item: number): number {
    return [...this.main, ...this.belt, ...this.armor].reduce((n, s) => n + (s.item === item ? s.count : 0), 0);
  }

  /** Count in the bag only (equipped tools are never consumed by crafting). */
  countInBag(item: number): number { return this.main.reduce((n, s) => n + (s.item === item ? s.count : 0), 0); }

  /** Removes items from the bag, last slots first. False (and nothing changes) if short. */
  removeFromBag(item: number, count: number): boolean {
    if (count <= 0) return true;
    if (this.countInBag(item) < count) return false;
    let remaining = count;
    for (let i = this.main.length - 1; i >= 0 && remaining > 0; i--) {
      const s = this.main[i];
      if (isEmpty(s) || s.item !== item) continue;
      const take = Math.min(remaining, s.count);
      this.main[i] = s.count - take > 0 ? { ...s, count: s.count - take } : emptyStack;
      remaining -= take;
    }
    this.version++;
    return true;
  }

  clone(): Inventory {
    const c = new Inventory(this.content);
    this.main.forEach((s, i) => { c.main[i] = s; });
    this.belt.forEach((s, i) => { c.belt[i] = s; });
    this.armor.forEach((s, i) => { c.armor[i] = s; });
    return c;
  }

  /** True if a tool of this kind is in the belt (tools in the grid don't count). */
  hasToolEquipped(kind: ToolKind): boolean {
    const slot = ToolBelt.slotFor(kind);
    return slot >= 0 && !isEmpty(this.belt[slot]) && toolOf(this.belt[slot].item, this.content) === kind;
  }

  /** Uses up one of what's in the offhand slot (a bow's arrow). */
  spendOffhand(): void {
    const s = this.belt[ToolBelt.offhand];
    if (!isEmpty(s)) this.set(ContainerId.Belt, ToolBelt.offhand, s.count > 1 ? { ...s, count: s.count - 1 } : emptyStack);
  }

  set(c: ContainerId, index: number, s: ItemStack): void {
    const arr = this.container(c);
    if (!arr || index < 0 || index >= arr.length) return;
    arr[index] = isEmpty(s) ? emptyStack : s;
    this.version++;
  }
}

/** Equipment slots bind "bind on equip" items the first time they go in. */
function bindIfEquipped(c: ContainerId, s: ItemStack, definitions: GameContent): ItemStack {
  return c !== ContainerId.Main && !isEmpty(s) && !s.bound && definitions.item(s.item)?.binding === Binding.OnEquip ? { ...s, bound: true } : s;
}

/** The weapon in the belt's weapon slot, if any. */
export function equippedWeapon(inv: Inventory): { item: number; stats: NonNullable<ItemDefinition['weapon']> } | null {
  const s = inv.belt[ToolBelt.weapon];
  const w = isEmpty(s) ? null : inv.content.item(s.item)?.weapon;
  return w ? { item: s.item, stats: w } : null;
}

/** What one attack does: a weapon's swing or shot, or a spell's missiles (one for a weapon), fanned across spreadDegrees. */
export interface AttackStats {
  damage: number; reach: number; arcDegrees: number; cooldownSeconds: number; knockback: number; manaCost: number;
  ammo: number; projectile: ProjectileStats | null; missiles: number; spreadDegrees: number; zone: HealZone | null;
}

/** The attack an item makes: a weapon's, or a spell scroll's (what a projectile in flight was shot by). */
export function attackStatsOf(def: ItemDefinition | undefined): AttackStats | null {
  if (def?.spell) return { ...def.spell, arcDegrees: 0, ammo: 0 };
  if (def?.weapon && def.weapon.weaponClass !== WeaponClass.Staff) return { ...def.weapon, missiles: 1, spreadDegrees: 0, zone: null };
  return null;
}

/**
 * The attack the equipped weapon makes (source: the item whose stats it uses, a staff's spell scroll), or why it
 * can't attack at all. The module sends the same notices.
 */
export function equippedAttack(inv: Inventory): { weapon: number; source: number; stats: AttackStats } | string {
  const weapon = equippedWeapon(inv);
  if (!weapon) return 'Equip a weapon in your tool belt (B) to attack.';
  if (weapon.stats.weaponClass !== WeaponClass.Staff) return { weapon: weapon.item, source: weapon.item, stats: attackStatsOf(inv.content.item(weapon.item))! };
  const off = inv.belt[ToolBelt.offhand], stats = isEmpty(off) || !offhandFits(weapon.item, off.item, inv.content) ? null : attackStatsOf(inv.content.item(off.item));
  return stats ? { weapon: weapon.item, source: off.item, stats } : 'Equip a spell scroll in your offhand slot to cast.';
}

/** An attack's cooldown in whole ticks (the server enforces it; the client paces swings by it). */
export const cooldownTicks = (w: { cooldownSeconds: number }) => Math.ceil(Math.fround(Math.fround(w.cooldownSeconds) * 30));

/**
 * Why an attack can't be paid for (the module sends the same notices), or null: a spell needs its mana, a bow its
 * arrows in the offhand slot. Swords are free.
 */
/** Why an attack can't be paid for: its cost (talents change it: TalentAttacks.cost), or its ammunition. */
export function attackCostProblem(w: AttackStats, inv: Inventory, mana: number, cost = { mana: w.manaCost, health: 0 }, health = Infinity): string | null {
  if (cost.mana > 0 && mana < cost.mana) return 'Not enough mana.';
  if (cost.health > 0 && health <= cost.health) return 'Not enough health.';
  if (w.ammo && inv.belt[ToolBelt.offhand].item !== w.ammo)
    return `Equip ${inv.content.item(w.ammo)?.name ?? 'ammunition'}s in your offhand slot to shoot.`;
  return null;
}

/** The directions (radians, float32) of an attack's missiles: fanned evenly across its spread, centred on angle. */
export function missileAngles(stats: Pick<AttackStats, 'missiles' | 'spreadDegrees'>, angle: number): number[] {
  const f = Math.fround, n = Math.max(1, Math.trunc(stats.missiles));
  if (n === 1) return [f(angle)];
  const spread = f(f(stats.spreadDegrees * f(Math.PI)) / 180);
  return Array.from({ length: n }, (_, i) => f(f(angle - f(spread / 2)) + f(f(spread * i) / (n - 1))));
}
